<template>
    <div class="guided-regulation-substance-row">
        <div class="guided-regulation-substance-row__main">
            <div class="guided-regulation-substance-row__content">
                <div class="guided-regulation-substance-row__match">
                    <label>
                        {{ translate('Component(ResearchReviewProcess):chemity-substance', 'Chemity substance') }}
                    </label>
                    <div class="guided-regulation-substance-row__match-control">
                        <SubstanceSelect
                            :value="summary"
                            :clearable="true"
                            :append-to-body="true"
                            :disabled="matching || (requireRegulationContext && !normalizedRegulationId)"
                            :search-context="searchContext"
                            :placeholder="translate('Component(Composition):search-placeholder', 'Search substances')"
                            :start-typing-text="translate('Component(Composition):search-label', 'Start typing to search')"
                            :no-options-text="translate('Component(Text):nothing-found', 'Nothing found')"
                            @input="$emit('select-link', $event)"
                        >
                            <template #selected-option="option">
                                <span class="guided-regulation-substance-row__selected-match">
                                    <strong>{{ option.name || option.title }}</strong>
                                    <small v-if="option.cas || option.ec" class="text-less-muted">
                                        <span v-if="option.cas">CAS: {{ option.cas }}</span>
                                        <span v-if="option.cas && option.ec"> · </span>
                                        <span v-if="option.ec">EC: {{ option.ec }}</span>
                                    </small>
                                </span>
                            </template>
                        </SubstanceSelect>
                        <BSpinner v-if="matching" small variant="primary" />
                    </div>
                </div>

                <div class="guided-regulation-substance-row__fields">
                    <div class="guided-regulation-substance-row__field guided-regulation-substance-row__field--name">
                        <label :for="inputId('name')">{{ translate('Component(Grid):column-name', 'Name') }}</label>
                        <BFormInput
                            :id="inputId('name')"
                            ref="nameInput"
                            :value="substance.name"
                            :state="nameViolationMessages.length ? false : null"
                            @input="updateSubstance('name', $event)"
                        />
                        <BFormInvalidFeedback
                            v-for="(message, index) in nameViolationMessages"
                            :key="`name-violation-${index}`"
                            force-show
                        >
                            {{ message }}
                        </BFormInvalidFeedback>
                    </div>
                    <div class="guided-regulation-substance-row__field">
                        <label :for="inputId('cas')">{{ translate('Component(SubstanceTable):column-cas', 'CAS number') }}</label>
                        <BFormInput
                            :id="inputId('cas')"
                            :value="substance.casNumber"
                            @input="updateSubstance('casNumber', $event || null)"
                        />
                    </div>
                    <div class="guided-regulation-substance-row__field">
                        <label :for="inputId('ec')">{{ translate('Component(SubstanceTable):column-ec', 'EC number') }}</label>
                        <BFormInput
                            :id="inputId('ec')"
                            :value="substance.ecNumber"
                            @input="updateSubstance('ecNumber', $event || null)"
                        />
                    </div>
                    <div class="guided-regulation-substance-row__field guided-regulation-substance-row__field--operator">
                        <label :for="inputId('operator')">{{ translate('Component(ResearchReviewProcess):operator', 'Operator') }}</label>
                        <BFormSelect
                            :id="inputId('operator')"
                            :value="concentration.operator"
                            :options="operatorOptions"
                            @input="updateConcentration('operator', $event)"
                        />
                    </div>
                    <div class="guided-regulation-substance-row__field guided-regulation-substance-row__field--value">
                        <label :for="inputId('value')">{{ translate('Component(Label):value', 'Value') }}</label>
                        <BFormInput
                            :id="inputId('value')"
                            :value="concentration.value"
                            type="number"
                            step="any"
                            @input="updateConcentration('value', normalizeNumber($event))"
                        />
                    </div>
                    <div class="guided-regulation-substance-row__field guided-regulation-substance-row__field--basis">
                        <label :for="inputId('basis')">{{ translate('Component(ResearchReviewProcess):concentration-basis', 'Basis') }}</label>
                        <BFormSelect
                            :id="inputId('basis')"
                            :value="concentration.basis"
                            :options="basisOptions"
                            @input="updateConcentration('basis', $event || null)"
                        />
                    </div>
                </div>

            </div>

            <BButton
                type="button"
                variant="outline-danger"
                size="sm"
                class="btn-icon guided-regulation-substance-row__remove"
                :title="translate(
                    'Component(ResearchReviewProcess):remove-global-substance',
                    'Remove this substance from the complete document',
                )"
                @click="$emit('remove')"
            >
                <FeatherIcon icon="Trash2Icon" size="16" />
            </BButton>
        </div>

    </div>
</template>

<script>
import {
    BButton,
    BFormInput,
    BFormInvalidFeedback,
    BFormSelect,
    BSpinner,
} from 'bootstrap-vue'
import SubstanceSelect from '@core/components/common/SubstanceSelect.vue'
import { CONCENTRATION_BASES } from '../../payload/document-compliance-payload'

const DEFAULT_CONCENTRATION = Object.freeze({
    operator: '<=',
    value: null,
    unit: 'w/w',
    basis: null,
})

export default {
    name: 'GuidedRegulationSubstanceRow',
    components: {
        BButton,
        BFormInput,
        BFormInvalidFeedback,
        BFormSelect,
        BSpinner,
        SubstanceSelect,
    },
    props: {
        rowKey: {
            type: String,
            required: true,
        },
        substanceIndex: {
            type: Number,
            default: null,
        },
        substance: {
            type: Object,
            required: true,
        },
        summary: {
            type: Object,
            default: null,
        },
        matching: {
            type: Boolean,
            default: false,
        },
        regulationId: {
            type: [Number, String],
            default: null,
        },
        requireRegulationContext: {
            type: Boolean,
            default: false,
        },
        defaultConcentrationBasis: {
            type: String,
            default: null,
        },
        violations: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            operatorOptions: ['<=', '<', '=', '>=', '>'],
        }
    },
    computed: {
        searchContext() {
            return this.normalizedRegulationId
                ? { regulationId: this.normalizedRegulationId }
                : {}
        },
        normalizedRegulationId() {
            const id = Number(this.regulationId)

            return Number.isInteger(id) && id > 0 ? id : null
        },
        concentration() {
            const concentration = {
                ...DEFAULT_CONCENTRATION,
                basis: this.defaultConcentrationBasis,
                ...(this.substance.concentration || {}),
            }

            return {
                ...concentration,
                basis: CONCENTRATION_BASES.includes(concentration.basis)
                    ? concentration.basis
                    : null,
            }
        },
        basisOptions() {
            return [{ value: null, text: '-' }].concat(CONCENTRATION_BASES.map(value => ({
                value,
                text: value,
            })))
        },
        nameViolationMessages() {
            return this.violationMessagesFor('name')
        },
    },
    methods: {
        violationMessagesFor(field) {
            const suffix = `[${field}]`

            return this.violations
                .filter(violation => violation
                    && String(violation.path || '').endsWith(suffix))
                .map(violation => violation.message)
                .filter(Boolean)
        },
        focusViolation(field) {
            const input = this.$refs[`${field}Input`]
            if (!input || typeof input.focus !== 'function') {
                return false
            }

            if (this.$el && typeof this.$el.scrollIntoView === 'function') {
                this.$el.scrollIntoView({ block: 'center' })
            }
            input.focus()

            return true
        },
        translate(key, fallback) {
            const translation = this.$t(key)

            return translation && translation !== key ? translation : fallback
        },
        inputId(field) {
            return `guided-regulation-substance-${this._uid}-${field}`
        },
        updateSubstance(key, value) {
            this.$emit('input', {
                ...this.substance,
                [key]: value,
            })
        },
        updateConcentration(key, value) {
            this.updateSubstance('concentration', {
                ...this.concentration,
                [key]: value,
            })
        },
        normalizeNumber(value) {
            if (value === '' || value === null || typeof value === 'undefined') {
                return null
            }

            const number = Number(value)
            return Number.isFinite(number) ? number : null
        },
    },
}
</script>

<style lang="scss" scoped>
.guided-regulation-substance-row {
    padding: 1rem;
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
}

.guided-regulation-substance-row + .guided-regulation-substance-row {
    margin-top: .75rem;
}

.guided-regulation-substance-row__main,
.guided-regulation-substance-row__fields,
.guided-regulation-substance-row__match-control {
    display: flex;
    align-items: flex-end;
}

.guided-regulation-substance-row__main {
    min-width: 0;
    align-items: flex-start;
    gap: .5rem;
}

.guided-regulation-substance-row__content {
    flex: 1 1 auto;
    min-width: 0;
}

.guided-regulation-substance-row__fields {
    flex: 1 1 auto;
    min-width: 0;
    gap: .5rem;
    overflow-x: auto;
    padding-bottom: .25rem;
}

.guided-regulation-substance-row__field {
    flex: 0 0 10rem;
}

.guided-regulation-substance-row__field--name {
    flex-basis: 14rem;
}

.guided-regulation-substance-row__field--operator {
    flex-basis: 6rem;
}

.guided-regulation-substance-row__field--value {
    flex-basis: 8rem;
}

.guided-regulation-substance-row__field--basis {
    flex-basis: 9rem;
}

.guided-regulation-substance-row__field label,
.guided-regulation-substance-row__match > label {
    display: block;
    margin-bottom: .25rem;
    font-size: .857rem;
    font-weight: 500;
}

.guided-regulation-substance-row__remove {
    flex: 0 0 auto;
}

.guided-regulation-substance-row__match {
    margin-bottom: .75rem;
}

.guided-regulation-substance-row__match-control {
    flex: 1 1 auto;
    min-width: 0;
    align-items: center;
    gap: .5rem;
}

.guided-regulation-substance-row__match-control > *:first-child {
    flex: 1 1 auto;
    min-width: 0;
}

.guided-regulation-substance-row__selected-match {
    display: inline-flex;
    min-width: 0;
    align-items: baseline;
    gap: .5rem;
}

</style>
